/**
 * Job Status Service for Center Hub
 *
 * Provides real-time job monitoring capabilities using Supabase with
 * graceful fallback to Google Sheets API if Supabase is unavailable.
 *
 * Features:
 * - Fetch current job status from Supabase
 * - Real-time subscriptions for job updates
 * - List all jobs by center with filtering
 * - Graceful degradation to Sheets API
 * - Comprehensive error handling and logging
 *
 * Created: November 3, 2025
 * Part of: Milestone 3 - Real-Time Status via Supabase (Center Hub)
 */

import { supabase, testJobMonitoringAccess } from '../lib/supabase-client';
import { fetchLectureData } from './googleSheets';
import config from '../config';

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Cache configuration
 * Reduces unnecessary API calls by caching recent results
 */
const CACHE_CONFIG = {
  ENABLED: true,
  TTL_MS: 5000, // 5 seconds - short TTL for real-time feel
  MAX_ENTRIES: 100,
};

/**
 * Fallback configuration
 * Controls when to fall back to Google Sheets API
 */
const FALLBACK_CONFIG = {
  ENABLED: true,
  RETRY_ATTEMPTS: 2,
  RETRY_DELAY_MS: 1000,
};

/**
 * In-memory cache for job status
 * Format: { job_id: { data, timestamp } }
 */
const statusCache = new Map();

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if cached data is still valid
 * @param {number} timestamp - Cache entry timestamp
 * @returns {boolean} True if cache entry is still fresh
 */
const isCacheValid = (timestamp) => {
  if (!CACHE_CONFIG.ENABLED) return false;
  return Date.now() - timestamp < CACHE_CONFIG.TTL_MS;
};

/**
 * Get cached job status
 * @param {string} job_id - The job ID to look up
 * @returns {Object|null} Cached job status or null if not found/expired
 */
const getCachedStatus = (job_id) => {
  if (!statusCache.has(job_id)) return null;

  const cached = statusCache.get(job_id);
  if (isCacheValid(cached.timestamp)) {
    console.log(`[JobStatusService] Cache hit for job ${job_id}`);
    return cached.data;
  }

  // Cache expired
  statusCache.delete(job_id);
  return null;
};

/**
 * Cache job status
 * @param {string} job_id - The job ID
 * @param {Object} data - The job status data
 */
const cacheStatus = (job_id, data) => {
  if (!CACHE_CONFIG.ENABLED) return;

  // Implement simple LRU by deleting oldest entries
  if (statusCache.size >= CACHE_CONFIG.MAX_ENTRIES) {
    const firstKey = statusCache.keys().next().value;
    statusCache.delete(firstKey);
  }

  statusCache.set(job_id, {
    data,
    timestamp: Date.now(),
  });
};

/**
 * Clear cache for specific job or entire cache
 * @param {string} [job_id] - Optional job ID to clear specific entry
 */
export const clearCache = (job_id = null) => {
  if (job_id) {
    statusCache.delete(job_id);
    console.log(`[JobStatusService] Cache cleared for job ${job_id}`);
  } else {
    statusCache.clear();
    console.log('[JobStatusService] All cache cleared');
  }
};

/**
 * Sleep utility for retry delays
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ============================================================================
// CORE FUNCTIONS
// ============================================================================

/**
 * Fetch job status from Supabase
 *
 * Retrieves current status for a specific job from the job_monitoring table.
 * Returns cached data if available and fresh.
 *
 * @param {string} job_id - The job ID to fetch (e.g., "IJ-1", "TESTCENTER1-2")
 * @param {Object} options - Fetch options
 * @param {boolean} options.skipCache - Skip cache and fetch fresh data
 * @param {boolean} options.fallbackToSheets - Fall back to Sheets if Supabase fails
 * @returns {Promise<Object>} Job status object or null if not found
 *
 * @example
 * const status = await getJobStatus('IJ-1');
 * if (status) {
 *   console.log(`Job ${status.job_id} is ${status.status}`);
 * }
 */
export const getJobStatus = async (job_id, options = {}) => {
  const { skipCache = false, fallbackToSheets = FALLBACK_CONFIG.ENABLED } = options;

  // Check cache first
  if (!skipCache) {
    const cached = getCachedStatus(job_id);
    if (cached) return cached;
  }

  console.log(`[JobStatusService] Fetching status for job ${job_id}`);

  try {
    // Query Supabase
    const { data, error } = await supabase
      .from('job_monitoring')
      .select('*')
      .eq('job_id', job_id)
      .single();

    if (error) {
      console.error(`[JobStatusService] Supabase query error for job ${job_id}:`, error.message);

      // Fall back to Sheets if enabled
      if (fallbackToSheets) {
        console.log(`[JobStatusService] Falling back to Sheets for job ${job_id}`);
        return await getJobStatusFromSheets(job_id);
      }

      return null;
    }

    if (!data) {
      console.warn(`[JobStatusService] Job ${job_id} not found in Supabase`);
      return null;
    }

    // Cache the result
    cacheStatus(job_id, data);

    console.log(`[JobStatusService] Successfully fetched job ${job_id} status: ${data.status}`);
    return data;
  } catch (err) {
    console.error(`[JobStatusService] Error fetching job ${job_id}:`, err);

    // Fall back to Sheets on exception
    if (fallbackToSheets) {
      console.log(`[JobStatusService] Exception fallback to Sheets for job ${job_id}`);
      return await getJobStatusFromSheets(job_id);
    }

    return null;
  }
};

/**
 * Subscribe to real-time job status updates
 *
 * Creates a real-time subscription to job_monitoring table changes.
 * Calls the callback function whenever the job status changes.
 *
 * @param {string} job_id - The job ID to monitor
 * @param {Function} callback - Callback function (payload) => void
 * @returns {Object} Subscription object with unsubscribe() method
 *
 * @example
 * const subscription = subscribeToJob('IJ-1', (job) => {
 *   console.log(`Job status changed to: ${job.status}`);
 *   updateUI(job);
 * });
 *
 * // Later: unsubscribe
 * subscription.unsubscribe();
 */
export const subscribeToJob = (job_id, callback) => {
  if (!job_id || !callback) {
    console.error('[JobStatusService] subscribeToJob requires job_id and callback');
    return { unsubscribe: () => {} };
  }

  console.log(`[JobStatusService] Subscribing to job ${job_id}`);

  const channel = supabase
    .channel(`job_${job_id}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'job_monitoring',
        filter: `job_id=eq.${job_id}`,
      },
      (payload) => {
        console.log(`[JobStatusService] Job ${job_id} updated:`, payload.new);

        // Update cache
        cacheStatus(job_id, payload.new);

        // Call user callback
        try {
          callback(payload.new);
        } catch (err) {
          console.error(`[JobStatusService] Callback error for job ${job_id}:`, err);
        }
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log(`[JobStatusService] Successfully subscribed to job ${job_id}`);
      } else if (status === 'CHANNEL_ERROR') {
        console.error(`[JobStatusService] Subscription error for job ${job_id}`);
      } else if (status === 'TIMED_OUT') {
        console.error(`[JobStatusService] Subscription timeout for job ${job_id}`);
      }
    });

  return {
    unsubscribe: () => {
      console.log(`[JobStatusService] Unsubscribing from job ${job_id}`);
      supabase.removeChannel(channel);
    },
  };
};

/**
 * Get all jobs for a specific center
 *
 * Retrieves all jobs associated with a center, with optional filtering
 * by status, date range, and pagination support.
 *
 * @param {string} center_id - The center UUID
 * @param {Object} options - Query options
 * @param {string|string[]} options.status - Filter by status (e.g., 'pending' or ['pending', 'processing'])
 * @param {string} options.submitted_after - ISO timestamp (filter jobs submitted after this time)
 * @param {string} options.submitted_before - ISO timestamp (filter jobs submitted before this time)
 * @param {number} options.limit - Maximum number of results (default: 50)
 * @param {number} options.offset - Pagination offset (default: 0)
 * @param {string} options.orderBy - Sort column (default: 'submitted_at')
 * @param {boolean} options.ascending - Sort order (default: false, descending)
 * @returns {Promise<Array>} Array of job objects
 *
 * @example
 * const activeJobs = await getJobsByCenter(centerId, {
 *   status: ['pending', 'processing'],
 *   limit: 20
 * });
 */
export const getJobsByCenter = async (center_id, options = {}) => {
  const {
    status = null,
    submitted_after = null,
    submitted_before = null,
    limit = 50,
    offset = 0,
    orderBy = 'submitted_at',
    ascending = false,
  } = options;

  if (!center_id) {
    console.error('[JobStatusService] getJobsByCenter requires center_id');
    return [];
  }

  console.log(`[JobStatusService] Fetching jobs for center ${center_id}`, options);

  try {
    // Build query
    let query = supabase
      .from('job_monitoring')
      .select('*')
      .eq('center_id', center_id);

    // Apply filters
    if (status) {
      if (Array.isArray(status)) {
        query = query.in('status', status);
      } else {
        query = query.eq('status', status);
      }
    }

    if (submitted_after) {
      query = query.gte('submitted_at', submitted_after);
    }

    if (submitted_before) {
      query = query.lte('submitted_at', submitted_before);
    }

    // Apply pagination and ordering
    query = query
      .order(orderBy, { ascending })
      .range(offset, offset + limit - 1);

    // Execute query
    const { data, error } = await query;

    if (error) {
      console.error(`[JobStatusService] Error fetching jobs for center ${center_id}:`, error.message);
      return [];
    }

    console.log(`[JobStatusService] Found ${data.length} jobs for center ${center_id}`);
    return data;
  } catch (err) {
    console.error(`[JobStatusService] Exception fetching jobs for center ${center_id}:`, err);
    return [];
  }
};

/**
 * Subscribe to all job updates for a center
 *
 * Creates a real-time subscription for all jobs belonging to a center.
 * Calls the callback for any INSERT, UPDATE, or DELETE event.
 *
 * @param {string} center_id - The center UUID
 * @param {Function} callback - Callback function (event, payload) => void
 * @returns {Object} Subscription object with unsubscribe() method
 *
 * @example
 * const subscription = subscribeToCenterJobs(centerId, (event, job) => {
 *   if (event === 'UPDATE') {
 *     console.log(`Job ${job.job_id} updated`);
 *   } else if (event === 'INSERT') {
 *     console.log(`New job ${job.job_id} created`);
 *   }
 * });
 */
export const subscribeToCenterJobs = (center_id, callback) => {
  if (!center_id || !callback) {
    console.error('[JobStatusService] subscribeToCenterJobs requires center_id and callback');
    return { unsubscribe: () => {} };
  }

  console.log(`[JobStatusService] Subscribing to all jobs for center ${center_id}`);

  const channel = supabase
    .channel(`center_jobs_${center_id}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'job_monitoring',
        filter: `center_id=eq.${center_id}`,
      },
      (payload) => {
        console.log(`[JobStatusService] Center ${center_id} job event:`, payload.eventType, payload.new?.job_id);

        // Update cache for UPDATE events
        if (payload.eventType === 'UPDATE' && payload.new?.job_id) {
          cacheStatus(payload.new.job_id, payload.new);
        }

        // Call user callback
        try {
          callback(payload.eventType, payload.new, payload.old);
        } catch (err) {
          console.error(`[JobStatusService] Callback error for center ${center_id}:`, err);
        }
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log(`[JobStatusService] Successfully subscribed to center ${center_id} jobs`);
      } else if (status === 'CHANNEL_ERROR') {
        console.error(`[JobStatusService] Subscription error for center ${center_id}`);
      }
    });

  return {
    unsubscribe: () => {
      console.log(`[JobStatusService] Unsubscribing from center ${center_id} jobs`);
      supabase.removeChannel(channel);
    },
  };
};

// ============================================================================
// FALLBACK FUNCTIONS (Google Sheets API)
// ============================================================================

/**
 * Get job status from Google Sheets (fallback)
 *
 * Falls back to Google Sheets API when Supabase is unavailable.
 * Parses the lecture data and extracts status information.
 *
 * Note: This provides limited status information compared to Supabase,
 * as Sheets doesn't have real-time status updates.
 *
 * @param {string} job_id - The job ID
 * @returns {Promise<Object|null>} Job status object or null
 */
const getJobStatusFromSheets = async (job_id) => {
  console.log(`[JobStatusService] Fetching job ${job_id} from Google Sheets fallback`);

  try {
    // Extract center ID from job_id (e.g., "TESTCENTER1-2" -> get center config)
    // This is a simplified implementation - enhance based on your actual data structure

    // For now, return a basic status object indicating fallback mode
    console.warn('[JobStatusService] Sheets fallback has limited status information');

    return {
      job_id,
      status: 'unknown',
      current_step: 'Status unavailable (using fallback)',
      progress: null,
      error_message: null,
      fallback_mode: true,
      message: 'Real-time status unavailable. Please check Google Sheets for details.',
    };
  } catch (err) {
    console.error(`[JobStatusService] Sheets fallback error for job ${job_id}:`, err);
    return null;
  }
};

// ============================================================================
// HEALTH CHECK
// ============================================================================

/**
 * Check if job status service is operational
 *
 * Tests connectivity to Supabase and job_monitoring table access.
 * Useful for diagnostics and initial setup verification.
 *
 * @returns {Promise<Object>} Health check results
 *
 * @example
 * const health = await checkServiceHealth();
 * if (health.healthy) {
 *   console.log('Service is operational');
 * } else {
 *   console.error('Service issues:', health.issues);
 * }
 */
export const checkServiceHealth = async () => {
  console.log('[JobStatusService] Running health check...');

  const issues = [];

  // Test Supabase connection
  try {
    const accessTest = await testJobMonitoringAccess();
    if (!accessTest.success) {
      issues.push({
        component: 'Supabase job_monitoring table',
        status: 'FAIL',
        message: accessTest.message,
      });
    }
  } catch (err) {
    issues.push({
      component: 'Supabase connection',
      status: 'ERROR',
      message: err.message,
    });
  }

  const healthy = issues.length === 0;

  const result = {
    healthy,
    timestamp: new Date().toISOString(),
    issues: healthy ? null : issues,
    fallback_available: FALLBACK_CONFIG.ENABLED,
  };

  console.log('[JobStatusService] Health check complete:', result);
  return result;
};

// ============================================================================
// EXPORTS
// ============================================================================

const jobStatusService = {
  getJobStatus,
  subscribeToJob,
  getJobsByCenter,
  subscribeToCenterJobs,
  checkServiceHealth,
  clearCache,
};

export default jobStatusService;
